import { fork, select, call, put, delay, takeLatest, cancelled, cancel, take } from "redux-saga/effects";
import {
  setExploreFilter, setExploreGeoFilter, setExploreLocation,
  setExploreSelectedIds, selectExploreMapPin, setExplorePropertyDetailsData, setExploreSearch, setExplorePropertyData,
  setResultsGridDataUpdate, setIsMainDataLoading, setIsSecondaryDataLoading, setExplorePinsAreVisible,
  getExploreSelectedProperties, setExplorePushProperties,
  setExploreIsLoading, setExplorePins, setExploreRequestedGroupLevel, setExplorePinDisplayType, setExploreRequestedPinDisplayType,
  setExploreSearchPin, setExploreChartsBucketsIsLoading, setExploreChartsBucketsData, setExploreMapType,
  setExploreSelectedShapeId, appNotify, setExplorePropertyDetailsBoxIsOpen,
  getPropertyDetailsDataForProspect, setProspectsPropertyDetailsData, fetchExplorePropertyData, cancelExploreDataFetch,
  updateSelectedExploreView, updateExplorePropertyDataObjectById, setExplorePropertyDetailsIsOpen, highlightExploreViewObject,
  fetchExplorePropertyUnderwrites, setExplorePropertyUnderwritesData, fetchUWData, fetchPostAcqTaskData, setSelectedPropertyUnderwrite,
  setIsFetchingPropertyUWHistory
} from 'store/actions';
import { convertZoomToPropertyGroupLevel, getManualPropertyGroupLevelForZoomLevel } from "api/rex/spatial";
import {
  exploreDataSelector, exploreSelectedViewSelector, propertyDataParamsSelector,
  exploreSelectedClusterIDsSelector, exploreSelectedClusterNamesSelector, exploreMapSelector,
  appSettingsSelector, isMainDataLoadingSelector, explorePropertyDetailsDataSelector, lastSelectedClusterSelector,
  propertyDataObjectByIdSelector, exploreSelectedIdsSelector
} from 'store/selectors';
import { defaultLocation } from 'store/explore/map';
import { getWithToken, postWithToken } from 'api/network';
import _ from "lodash";
import { Constants } from 'utilities/constants';

function* fetchUpdatedData(objectId, highlightPins) {
  try {
    // console.log(objectId)
    const appSettings = yield select(appSettingsSelector);
    const { searchContext } = yield select(exploreDataSelector);
    const mapState = yield select(exploreMapSelector);
    const { requestedGroupLevel, bounds, zoom, filter, filterGeo, requestedPinDisplayType, pinDisplayType } = yield select(propertyDataParamsSelector, objectId);
    let convertedRequestLevel;
    const groupLevelByZoom = convertZoomToPropertyGroupLevel(zoom)
    if (requestedGroupLevel === 'auto') {
      convertedRequestLevel = groupLevelByZoom;
    } else {
      if (getManualPropertyGroupLevelForZoomLevel(zoom).find(type => type.value === requestedGroupLevel)?.enabled) { // Use user selected type if it is allowed for current zoom level
        convertedRequestLevel = requestedGroupLevel;
      } else {
        yield put(appNotify('Property Data - zoom exceeded for selected geo type, switching to auto', 'info'));
        yield put(setExploreRequestedGroupLevel(objectId, 'auto', true)); // Else revert to auto mode if zoom no longer fits
        return;
      }
    }

    const fromSearch = searchContext != null;
    const url = `/api/explore/GetClusters`;

    let columns = appSettings.GetClusters.MainColumns;
    const pinDisplayField = requestedPinDisplayType?.dataField ?? pinDisplayType?.dataField;
    if (pinDisplayField != null){
      columns = [...columns, pinDisplayField ]
    }
    const autoBreakoutThreshold = requestedGroupLevel === 'auto' || requestedGroupLevel === 'Property' ? mapState.markerOptions?.autoBreakoutThreshold : null;

    const { propertyDataDisplay, objects } = yield select(exploreSelectedViewSelector);
    const object = objects.find(obj => obj.id === objectId);
    let secondaryColumns = appSettings.GetClusters.SecondaryColumns;
    if (object?.gridState?.state) {
      secondaryColumns = secondaryColumns.filter(secCol => object.gridState.state.columns.find(stateCol => stateCol.dataField === secCol)?.visible);
    }

    const mainRequest = {
      zoomLevel: zoom,
      groupLevelByZoom,
      clusterType: convertedRequestLevel,
      autoBreakoutThreshold,
      bounds,
      filter,
      filterGeo: JSON.stringify(filterGeo),
      columnsToSelect: columns
    };

    const secondaryRequest = {
      zoomLevel: zoom,
      groupLevelByZoom,
      clusterType: convertedRequestLevel,
      autoBreakoutThreshold,
      bounds,
      filter,
      filterGeo: JSON.stringify(filterGeo),
      columnsToSelect: secondaryColumns
    };
    
    yield fork(getClustersMainCall, objectId, mainRequest, requestedGroupLevel, url, fromSearch, highlightPins);
    if (objectId === propertyDataDisplay.selectedSourceId) { // Only run second call if viewing the same data on grid
      yield fork(getClustersSecondaryCall, objectId, secondaryRequest, url);
    }
  } catch (error) {
    console.log(error);
    yield put(appNotify('Error occurred while fetching clusters', 'error'));
  }
}

function* getClustersMainCall(objectId, requestDto, requestedGroupLevel, url, fromSearch, highlightPins) {
  const propertyDataObject = yield select(propertyDataObjectByIdSelector, objectId);
  try {
    // yield put(setExploreIsLoading(true, "MAP", "Loading Map Data..."));
    yield put(setIsMainDataLoading(objectId, true));
    if (propertyDataObject.isErrored) {
      yield put(updateExplorePropertyDataObjectById(objectId, { isErrored: false }));
    }
    console.log('getClusters main call', requestDto);
    const response = yield postWithToken(url, requestDto);
    const parsed = yield response.json();

    const { data: clustersData, isBreakOut, propertiesCount } = parsed;

    const isRevertToAuto = requestedGroupLevel === 'Property' && !isBreakOut;
    const newClusterType = isRevertToAuto ? requestDto.groupLevelByZoom : isBreakOut ? 'Property' : requestDto.clusterType;
    if (isRevertToAuto) {
      yield put(appNotify('Property Data - Property count exceeded limit for manual selection, switching to auto', 'info'));
      yield put(setExploreRequestedGroupLevel(objectId, 'auto', false)); // Else revert to auto mode if zoom no longer fits
    }

    yield put(setExplorePropertyData(objectId, clustersData, propertiesCount, !isBreakOut, newClusterType, fromSearch));
    if (propertyDataObject.requestedPinDisplayType?.dataField != null) {
      yield put(setExplorePinDisplayType(objectId, propertyDataObject.requestedPinDisplayType));
    }

    yield put(setExplorePins(clustersData));

    let selectedClusterIDs = yield select(exploreSelectedClusterIDsSelector); // Only returns IDs that are present in current data
    const selectedIds = yield select(exploreSelectedIdsSelector);
    const idsChanged = selectedIds && !_.isEqual(selectedIds.toSorted(), selectedClusterIDs.toSorted());
    if (idsChanged) {
      yield put(setExploreSelectedIds({ ids: selectedClusterIDs, from: 'Saga' })); // Update selected IDs to potentially remove any previously selected that are no longer in view
    }
    if (fromSearch) {
      yield call(onSearchSet);
      yield put(setExplorePropertyData(objectId, clustersData, propertiesCount, !isBreakOut, newClusterType, false));
      yield put(setExploreSearch(null));
    }
    if (highlightPins) {
      yield put(highlightExploreViewObject(objectId, Constants.ViewsObjectType.PROPERTYDATA));
    }
  } catch (error) {
    console.log(error);
    yield put(appNotify('Error occurred in main clusters fetch', 'error'));
    yield put(updateExplorePropertyDataObjectById(objectId, { isErrored: true }));
  } finally {
    yield put(setIsMainDataLoading(objectId, false));
    // yield put(setExploreIsLoading(false, "MAP"));
    if (propertyDataObject.requestedPinDisplayType?.dataField != null) {
      yield put(setExploreRequestedPinDisplayType(objectId, null));
    }
  }
}

function* getClustersSecondaryCall(objectId, requestDto, url) {
  try {
    console.log('getClusters secondary call', requestDto);
    yield put(setIsSecondaryDataLoading(objectId, true));
    // yield delay(5000)
    const response = yield postWithToken(url, requestDto);

    yield syncronizeMainAndSecondaryCalls(objectId);
    if (response.ok) {
      const parsed = yield response.json();
      yield put(setResultsGridDataUpdate(parsed.data));
    } else { 
      console.log(response.status, response.statusText);
    }
  } catch (error) {
    console.log(error);
    yield put(appNotify('Error occurred in secondary clusters fetch', 'error'));
    yield put(updateExplorePropertyDataObjectById(objectId, { isErrored: true }));
  } finally {
    yield put(setIsSecondaryDataLoading(objectId, false));
  }
}

function* syncronizeMainAndSecondaryCalls(objectId)
{
  let waitingForMainDataMs = 10000;
  let isMainIndicatorVisible = yield select(isMainDataLoadingSelector, objectId);
  while(isMainIndicatorVisible && waitingForMainDataMs > 0)
  {
    // Keed polling status of first call until it changes or timeout
    yield delay(500);
    waitingForMainDataMs -= 500;
    isMainIndicatorVisible = yield select(isMainDataLoadingSelector, objectId);
    console.log('Second call is pending. Timeout in: ', waitingForMainDataMs);
  }
  if (isMainIndicatorVisible)
  {
    yield put(appNotify(`Timeout waiting for main GetClusters call. Secondary call is canceled.`, 'error'));
  }
} 

function* fetchPropertyDetailsDataForExploreProperty(asgPropID, withDetails = false) {
  yield put(setExploreIsLoading(true, "PROPERTY", "Loading Property Data..."));
  try {
    const url = `/api/explore/GetProperty?asgPropID=${asgPropID}${withDetails ? '&withDetails=true' : ''}`;
    const response = yield getWithToken(url);
    if (response.ok) {
      const data = yield response.json();
      yield put(setExploreIsLoading(false, "PROPERTY"));
      return data;
    } else {
      console.log(response.status, response.statusText);
      yield put(setExploreIsLoading(false, "PROPERTY"));
      return null;
    }
  } catch (error) {
    console.log(error);
    yield put(appNotify(`Error occurred while fetching property data - ${error.message}`, 'error'));
    yield put(setExploreIsLoading(false, "PROPERTY"));
    return null;
  } finally {
    if (yield cancelled()) {
      console.log('cancelled');
      yield put(setExploreIsLoading(false, "PROPERTY"));
    }
  }
}

function* fetchPropertyDetailsDataByAsgPropID(asgPropID) {
  try {
    const url = `/api/propertyIndicatives?asgPropID=${asgPropID}`;
    const response = yield getWithToken(url);
    if (response.ok) {
      const data = yield response.json();
      console.log(data);
      return data;
    }
  } catch (error) {
    console.log(error);
    yield put(appNotify(`Error occurred while fetching property data - ${error.message}`, 'error'));
    return null;
  } finally {
    if (yield cancelled()) {
      console.log('cancelled');
    }
  }
}

function* fetchClusterPropertiesData() {
  yield put(setExplorePushProperties([]));
  yield put(setExploreIsLoading(true, "PROPERTY", "Loading Property Data..."));
  const { requestedGroupLevel, bounds, filter, filterGeo } = yield select(propertyDataParamsSelector);

  const exploreSelectedClusterNames = yield select(exploreSelectedClusterNamesSelector);
  console.log(exploreSelectedClusterNames)
  const clusterNamesFilter = exploreSelectedClusterNames;

  let clustersData = [];
  try {
    // var t0 = performance.now();
    const requestDto = {
      clusterType: requestedGroupLevel,
      filterClusters: clusterNamesFilter,
      bounds,
      filter,
      filterGeo: JSON.stringify(filterGeo)
    };
    console.log('getClusterProperties', requestDto);
    const url = `/api/explore/GetClusterProperties`;
    const response = yield postWithToken(url, requestDto);

    // var t1 = performance.now();
    //console.log("getClusters took " + ((t1 - t0) / 1000) + " seconds.");

    if (response.ok) {
      const data = yield response.json();
      clustersData = data;
    } else {
      console.log(response.status, response.statusText);
    }
  } catch (error) {
    console.log(error);
    yield put(appNotify('Error occurred while fetching cluster properties data', 'error'));
  }
  yield put(setExplorePushProperties(clustersData.map(cluster => cluster?.isProperty ? cluster?.property : cluster?.properties)));
  
  yield put(setExploreIsLoading(false, "PROPERTY"));
}

function* fetchBucketsData({ type, payload }) {
  if (type === 'exploreViewsSlice/updateSelectedExploreView') {
    if (!payload?.update?.propertyDataDisplay) return;
  }
  const selectedView = yield select(exploreSelectedViewSelector);
  const selectedSourceVisible = selectedView?.propertyDataDisplay.selectedSourceId != null &&
    selectedView.objects.find(obj => obj.id === selectedView.propertyDataDisplay.selectedSourceId)?.visible;
  if (!selectedView?.propertyDataDisplay || selectedView.propertyDataDisplay.selectedSourceId == null || !selectedSourceVisible) return;
  const { visible, selectedSourceId, type: chartType, chartsSelectedDistributionType } = selectedView.propertyDataDisplay;
  if (!visible || chartType !== 'Charts') return;
  if (type === 'exploreDataSlice/setExplorePropertyData') {
    if (payload.objectId !== selectedSourceId) return;
  }
  let bucketsData = [];
  yield put(setExploreChartsBucketsIsLoading(true));
  const { bounds, filter, filterGeo } = yield select(propertyDataParamsSelector, selectedSourceId);
  try {
    var t0 = performance.now();

    const requestDto = {
      bucketType: chartsSelectedDistributionType,
      bounds,
      filter,
      filterGeo: JSON.stringify(filterGeo)
    }
    console.log('fetchBucketsData', requestDto);
    const url = `/api/explore/GetBucketStats`;
    const response = yield postWithToken(url, requestDto);

    var t1 = performance.now();
    console.log("getBucketStats took " + ((t1 - t0) / 1000) + " seconds.");

    if (response.ok) {
      const data = yield response.json();
      bucketsData = data;
    } else {
      console.log(response.status, response.statusText);
    }
  } catch (error) {
    console.log(error);
    yield put(appNotify('Error occurred while fetching buckets data', 'error'));
  }
  yield put(setExploreChartsBucketsData(bucketsData));
  yield put(setExploreChartsBucketsIsLoading(false));
}

function* onSearchSet() {
  const { searchContext } = yield select(exploreDataSelector);
  if (!searchContext?.groupLevel) return;
  const { propertyDataObjects } = yield select(exploreDataSelector);
  const searchClusterId = searchContext.groupLevel === "Property"
    ? searchContext.streetNumber + " " + searchContext.streetName.toUpperCase()
    : searchContext.id;
  let foundPin = null;
  const foundObjectId = Object.keys(propertyDataObjects).find(objectId => {
    const pdo = propertyDataObjects[objectId];
    if (!pdo.pinsAreVisible) return false;
    const pin = pdo.propertyData.find(el => {
      return searchClusterId === el.clusterID || searchClusterId === el.clusterName; 
    });
    if (pin) {
      foundPin = pin;
      return true;
    }
    return false;
  });
  if (foundObjectId == null) {
    yield put(setExploreSelectedIds(null));
    yield put(setExploreSearchPin({ lat: searchContext.lat, lng: searchContext.lng }));
  } else {
    const selectionObj = { objectId: parseInt(foundObjectId) || foundObjectId, clusterID: foundPin.clusterID };
    yield put(setExploreSelectedIds({ ids: [selectionObj], mapSelection: selectionObj, from: 'Map' }));
    yield put(setExploreSearchPin(null));
  }
}

const checkShouldFetch = (action, takeArray) => { // Don't take the action and run the saga if shouldFetch is false
  let shouldTake = false;
  const toString = takeArray.map(el => el.toString());
  if (!toString.includes(action.type)) return false;

  if (action.payload.shouldFetch) {
    shouldTake = true;
  }
  return shouldTake;
}

export const takeLatestByObjectId = (patternOrChannel, saga, ...args) => fork(function*() {
  let lastTasks = {}; // hold a reference to each forked saga identified by the objectId

  while (true) {
    const action = yield take(patternOrChannel);
    // if there is a forked saga running with the same objectId, cancel it.
    if (lastTasks[action.payload.objectId]) {
      yield cancel(lastTasks[action.payload.objectId]);
    }

    lastTasks[action.payload.objectId] = yield fork(saga, ...args.concat(action));
  }
});

export function* explorePropertyDataSaga() {
  yield takeLatestByObjectId(
    action => checkShouldFetch(action, [fetchExplorePropertyData, setExploreRequestedGroupLevel]),
    function* ({ type, payload }) {
      yield delay(100); // Delay to allow 'finally' blocks in cancelled tasks to complete
      if (type === 'exploreDataSlice/setExploreFilter' || type === 'exploreDataSlice/setExploreGeoFilter') {
        if (payload?.filterByImportId) {
          const mapState = yield select(exploreMapSelector);
          if (mapState.mapType !== 'roadmap') {
            yield put(setExploreMapType('roadmap'));
          }
          if (mapState.filterGeo) { // Clear any existing geo filter
            yield put(setExploreGeoFilter(null, false));
          }
          yield put(setExploreLocation(defaultLocation, false, true));
        } else if (payload.shouldFetch) {
          yield call(fetchUpdatedData);
        }
        return;
      }

      if (
        // type === 'exploreMapSlice/setExploreLocation' || 
        type === 'exploreDataSlice/fetchExplorePropertyData' || type === 'exploreDataSlice/setExploreRequestedGroupLevel' || type === 'exploreDataSlice/setExplorePinsAreVisible') {
        const { shouldFetch } = payload;
        if (shouldFetch) {
          // yield delay(500);
          const dataTask = yield fork(() => fetchUpdatedData(payload.objectId, payload.highlightPins));
          yield take(cancelExploreDataFetch);
          yield cancel(dataTask);
        }
        return;
      }
    }
  );

  yield takeLatest(setExplorePinsAreVisible, function* ({ payload }) { // Show/hide property details data when toggling visibility
    const propertyDetailsData = yield select(explorePropertyDetailsDataSelector);
    if (!propertyDetailsData) return;
    if (propertyDetailsData.objectId === payload.objectId) {
      yield put(setExplorePropertyDetailsBoxIsOpen(payload.pinsAreVisible));
    }
  });

  yield takeLatestByObjectId(action => checkShouldFetch(action, [fetchExplorePropertyData, setExploreRequestedGroupLevel, setExploreFilter, setExploreGeoFilter, setExplorePinsAreVisible, setExploreRequestedPinDisplayType]),
    function* ({ type, payload }) { // Setting pinDisplayType runs a data fetch similar to getClustersMainCall, but on it's own takeLatest, to allow it to be cancelled by other data actions, but prevent it cancelling the secondary call in the normal fetchUpdatedData.
      if (type !== 'exploreDataSlice/setExploreRequestedPinDisplayType' || payload == null) return;
      const { objectId, requestedPinDisplayType } = payload;
      if (!requestedPinDisplayType) return;
      try {  
        yield put(setIsMainDataLoading(objectId, true));
        const propertyDataObject = yield select(propertyDataObjectByIdSelector, objectId);
        if (propertyDataObject.isErrored) {
          yield put(updateExplorePropertyDataObjectById(objectId, { isErrored: false }));
        }
        // yield put(setExploreIsLoading(true, "MAP", "Fetching Pin Labels..."));
        const { requestedGroupLevel, bounds, zoom, filter, filterGeo } = yield select(propertyDataParamsSelector, objectId);
        let convertedRequestLevel;
        if (requestedGroupLevel === 'auto') {
          convertedRequestLevel = convertZoomToPropertyGroupLevel(zoom);
        } else {
          if (getManualPropertyGroupLevelForZoomLevel(zoom).find(type => type.value === requestedGroupLevel)?.enabled) { // Use user selected type if it is allowed for current zoom level
            convertedRequestLevel = requestedGroupLevel;
          } else {
            yield put(appNotify('Geographic Data - zoom exceeded for selected geo type, switching to auto', 'info'));
            yield put(setExploreRequestedGroupLevel(objectId, 'auto', true)); // Else revert to auto mode if zoom no longer fits
            return;
          }
        }
          const appSettings = yield select(appSettingsSelector);
          let columns = [...appSettings.GetClusters.MainColumns, requestedPinDisplayType.dataField];
          const mapState = yield select(exploreMapSelector);
          const autoBreakoutThreshold = mapState.markerOptions?.autoBreakoutThreshold;
    
          const requestDto = {
            zoomLevel: zoom,
            clusterType: convertedRequestLevel,
            autoBreakoutThreshold,
            bounds,
            filter,
            filterGeo: JSON.stringify(filterGeo),
            columnsToSelect: columns
          };
          
          let clustersData = [];
          let { propertiesCount, isAggregateData, fromSearch } = propertyDataObject;
          let newClusterType = requestDto.clusterType;
          const url = `/api/explore/GetClusters`;
          console.log('pinDisplayType call', requestDto);
          const response = yield postWithToken(url, requestDto);
          if (response.ok) {
            const parsed = yield response.json();
            clustersData = parsed.data;
            propertiesCount = parsed.propertiesCount;
            isAggregateData = !parsed.isBreakOut;
            newClusterType = parsed.isBreakOut ? 'Property' : newClusterType;
          } else {
            console.log(response.status, response.statusText);
          }
          // yield delay(5000)

          yield put(setExplorePropertyData(objectId, clustersData, propertiesCount, isAggregateData, newClusterType, fromSearch));
          yield put(setExplorePinDisplayType(objectId, requestedPinDisplayType));
          // yield put(setExploreIsLoading(false, "MAP", 'fromPinDispayType'));
      } catch (error) {
        console.log(error);
        yield put(appNotify('Error occurred while fetching clusters', 'error'));
        yield put(updateExplorePropertyDataObjectById(objectId, { isErrored: true }));
        // yield put(setExploreIsLoading(false, "MAP", 'fromPinDispayType'));
      } finally {
        yield put(setIsMainDataLoading(objectId, false));
        yield put(setExploreRequestedPinDisplayType(objectId, null));
      }
    }
  );

  yield takeLatest(getPropertyDetailsDataForProspect, function* ({ payload }) {
    const { asgPropID, callback } = payload;
    yield put(setProspectsPropertyDetailsData(null)); //null out property details before fetch so we don't show previous property details before fetch
    // setProspectsPropertyDetailsData
    const res = yield call(fetchPropertyDetailsDataByAsgPropID, asgPropID);
    if (!res) { // Call failed
      callback && callback(false); // Pass false to prevent opening property details modal
      return;
    }
    if (res.success) { // Call succeeded and found property
      yield put(setProspectsPropertyDetailsData(res.data)); // Set data so modal will use fetched data
    } else { // Call succeeded but found no property
      yield put(appNotify(`Error occurred while fetching property data - ${res.errorMessage}`, 'warning')); // Notify user of property not found. Modal will use prospect grid data
    }
    callback && callback(true); // Pass true to open modal
  })

  yield takeLatest(getExploreSelectedProperties, fetchClusterPropertiesData);

  yield takeLatest([setExploreSelectedIds, selectExploreMapPin], function* () {
    yield delay(100); // debounce cluster selection by 100ms
    const cluster = yield select(lastSelectedClusterSelector);
    const asgPropID = cluster?.asgPropID;
    if (!asgPropID) {
      yield put(setExplorePropertyDetailsData(null));
      return;
    }
    const propertyDetailsData = yield select(explorePropertyDetailsDataSelector);
    if (propertyDetailsData?.asgPropID === asgPropID) return; // No need to refetch if lastSelected id hasn't changed
    let newPropertyDetailsData = null;

    // if (property) {
    //   if (property.capRate) {
    //     propertyDetailsData = yield call(fetchPropertyDetailsDataForExploreProperty, asgPropID);
    //   } else {
    //     propertyDetailsData = property;
    //   }
    // } else {
      newPropertyDetailsData = yield call(fetchPropertyDetailsDataForExploreProperty, asgPropID);
    // }
    yield put(setExplorePropertyDetailsData({ ...newPropertyDetailsData, objectId: cluster.objectId }));
  });

  yield takeLatest(setExplorePropertyDetailsIsOpen, function* () {
    const mapState = yield select(exploreMapSelector);
    if (!mapState.propertyDetailsIsOpen) return;
    
    const cluster = yield select(lastSelectedClusterSelector);
    const asgPropID = cluster?.asgPropID;
    const propertyDetailsData = yield select(explorePropertyDetailsDataSelector);

    if (propertyDetailsData?.asgPropID === asgPropID && propertyDetailsData.hasDetail) return; // No need to refetch if lastSelected id hasn't changed
    let newPropertyDetailsData = null;

    newPropertyDetailsData = yield call(fetchPropertyDetailsDataForExploreProperty, asgPropID, true);
    yield put(setExplorePropertyDetailsData({ ...newPropertyDetailsData, hasDetail: true, objectId: cluster.objectId }));
  });

  yield takeLatest([updateSelectedExploreView, setExplorePropertyData], fetchBucketsData);

  yield takeLatest(setExploreSelectedShapeId, function* ({ payload }) {
    if (!payload.shouldSelectPin) return;
    const { selectedShapeId } = payload;
    const { propertyDataObjects } = yield select(state => state.exploreData);
    // const groupLevel = yield select(state => state.exploreData.groupLevel);
    // const { geoType } = yield select(state => state.exploreShapesData);
    // const { pins } = yield select(state => state.exploreMap);
    // if (!selectedShapeId || !groupLevel || !geoType || !pins) return;
    if (!Object.keys(propertyDataObjects).length) return;
    // const foundPin = pins.find(pin => {
    //   return selectedShapeId === pin.clusterID || selectedShapeId === pin.clusterName;
    // });

    let foundPin = null;
    const foundObjectId = Object.keys(propertyDataObjects).find(objectId => {
      const pdo = propertyDataObjects[objectId];
      if (!pdo.pinsAreVisible) return false;
      const pin = pdo.propertyData.find(el => {
        return selectedShapeId === el.clusterID || selectedShapeId === el.clusterName; 
      });
      if (pin) {
        foundPin = pin;
      }
      return pin;
    })
    console.log(foundObjectId, foundPin)
    if (foundPin) {
      yield put(selectExploreMapPin({ objectId: parseInt(foundObjectId) || foundObjectId, clusterID: foundPin.clusterID }));
    } else {
      // yield put(setExploreSelectedIds(null)); // Don't deselect previous pin if new pin not found
    }
  });

  yield takeLatest(setExploreSearch, function* ({ payload }) {
    if (!payload.forceSearchSet) return;
    console.log('saga calling onSearchSet')
    yield call(onSearchSet);
  });

  yield takeLatest(fetchExplorePropertyUnderwrites, function* ({ payload }) {
    const { asgPropID, shouldFetchUW } = payload;
    try {
      yield put(setIsFetchingPropertyUWHistory(true));
      const url = `/api/explore/GetUnderwrites?asgPropID=${asgPropID}`;
      const response = yield getWithToken(url);
      if (response.ok) {
        const data = yield response.json();
        yield put(setExplorePropertyUnderwritesData(data));
        const newest = data[0];
        if (shouldFetchUW && newest) { 
          yield put(setSelectedPropertyUnderwrite(newest));
          if (newest.module === 'Acq') {
            yield put(fetchUWData(newest.id, null, []));
          } else {
            yield put(fetchPostAcqTaskData(newest.id));
          }
        }
      }
    } catch (error) {
      console.log(error);
      yield put(appNotify(`Error occurred while fetching property underwrite data - ${error.message}`, 'error'));
      
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
      yield put(setIsFetchingPropertyUWHistory(false));
    }
  });

  yield takeLatest(setSelectedPropertyUnderwrite, function* ({ payload }) {
    if (payload.module === 'Acq') {
      yield put(fetchUWData(payload.id, null, []));
    } else {
      yield put(fetchPostAcqTaskData(payload.id));
    }
  });
}
